import { useState, useEffect, useContext } from 'react';
import { useLocation, RouterContext, goTo } from 't-lib-ui/router';
import { Spinner } from 't-lib-ui/widgets';
import { AppState } from '../context/general';
import { getPublicFeedByUrl } from '../connector/api';
import { markAnonFromShare, applyFirstFeedDefaults } from '../connector/anon-onboarding';
import { RestCatalog, RestUserFeed } from '../schema-copy/api-schema';
import ShareHeader from '../components/ShareHeader';
import FeedTile from '../components/FeedTile';
import FeedPreview from '../components/FeedPreview';
import LegalFooter from '../components/LegalFooter';
import './SharePage.css';
import { sanitizeNoWhitespace } from 't-lib-ui/connector';

// Public landing page for links of the form /add?url=<feed url>. Lets a (possibly
// logged-out) reader subscribe to an EXISTING, non-private Downread feed by its RSS/Atom
// URL. The server only does a DB lookup here — it never fetches the URL or creates a
// feed — so new feeds cannot be created through this page. Logged-in visitors are sent
// into the in-app add flow, which can create feeds.
const SubscribeFeedPage: React.FC = () => {
  const location = useLocation();
  const url = location.searchParams.get('url') ?? '';
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  const isLoggedIn = appState.state.loginState == 'ok';
  const [feed, setFeed] = useState<RestUserFeed | RestCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [trying, setTrying] = useState(false);

  // Logged-in users go straight into the regular add flow, which handles both
  // "feed already exists -> follow" and "not found -> create".
  useEffect(() => {
    if (isLoggedIn)
      goTo(routerCtx, url ? `/a/add?url=${encodeURIComponent(url)}` : '/a/add', 'replace');
  }, [isLoggedIn, url, routerCtx]);

  useEffect(() => {
    if (isLoggedIn) return;
    if (!url) {
      setError('No feed URL was provided.');
      setLoading(false);
      return;
    }
    let cancelled = false;

    const loadFeed = async () => {
      setLoading(true);
      setError(null);
      try {
        const feedData = await getPublicFeedByUrl(url);
        if (!cancelled) setFeed(feedData);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFeed();
    return () => { cancelled = true; };
  }, [url, isLoggedIn]);

  // Following works the same for anonymous readers — the anon engine keeps the subscription in
  // localStorage — so reflect the live subscribed state from the active engine.
  const isSubscribed = () => !!feed && feedEngine.getFeeds().some(f => f.id == feed.id);
  const [subscribed, setSubscribed] = useState(false);
  useEffect(() => {
    setSubscribed(isSubscribed());
    return feedEngine.onFeedsChange(() => setSubscribed(isSubscribed()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, feedEngine]);

  // Logged-in visit is mid-redirect to /a/add (effect above) — show a spinner until that lands.
  if (isLoggedIn)
    return (
      <div className="sharePage">
        <ShareHeader />
        <div className="sharePageContent">
          <div className="sharePageLoading">
            <Spinner spinning />
            <span>Opening Downread...</span>
          </div>
        </div>
      </div>
    );

  const feedName = feed?.shortTitle || feed?.title;
  const displayUrl = sanitizeNoWhitespace(url, 256);

  // "Try now": no account needed — follow this feed in the active (anon) engine and drop straight
  // into the reader so the visitor starts reading immediately.
  const handleTryNow = async () => {
    if (!feed || trying) return;
    setTrying(true);
    try {
      feedEngine.registerFeedInfo(feed);
      await feedEngine.subscribe(feed.id);
      applyFirstFeedDefaults(feedEngine, feed.id);
      markAnonFromShare();
      goTo(routerCtx, `/a/feed/${feed.id}`);
    } catch (e) {
      console.error('Try now failed:', e);
      setTrying(false);
    }
  };

  // Follow from the tile: once the subscribe lands, drop the reader straight into the feed. Only
  // navigate on follow (now subscribed), not when the action was an unfollow.
  const handleTileAction = () => {
    if (feed && feedEngine.getFeeds().some(f => f.id == feed.id)) {
      applyFirstFeedDefaults(feedEngine, feed.id);
      markAnonFromShare();
      goTo(routerCtx, `/a/feed/${feed.id}`);
    }
  };

  return (
    <div className="sharePage">
      <ShareHeader />
      <div className="sharePageContent">
        {loading && (
          <div className="sharePageLoading">
            <Spinner spinning />
            <span>Looking up feed...</span>
          </div>
        )}

        {!loading && error && (
          <div className="sharePageError">
            <h2>This feed isn't on Downread yet</h2>
            {displayUrl && <p className="shareErrorUrl">{displayUrl}</p>}
            <p>
              We can only open feeds here that a Downread user already subscribes to.
              Sign up or log in to add this feed to your personal reader.
            </p>
            <div className="shareErrorActions">
              <a href="/signup" className="shareBannerCta">Sign up free</a>
              <a href="/login" className="shareBannerCtaSecondary">Log in</a>
            </div>
          </div>
        )}

        {feed && !loading && (
          <>
            <div className="shareBanner">
              <div className="shareBannerText">
                Read <strong>{feedName}</strong> on{' '}
                <a href="https://downread.com">Downread</a>, a RSS feed reader.{' '}
                <strong>No account needed</strong>. Start reading right away.
              </div>
              <div className="shareBannerActions">
                <button type="button" className="shareBannerCta" onClick={handleTryNow} disabled={trying}>
                  {trying ? 'Opening…' : 'Try now'}
                </button>
              </div>
            </div>

            <div className="shareFeedTileWrapper">
              <FeedTile feed={feed} subscribed={subscribed} showPrivacyNote onAction={handleTileAction} />
            </div>

            <FeedPreview feed={feed} maxPosts={20} linkToSharePosts />

            <div className="shareBanner bottom">
              <div className="shareBannerText">
                Read <strong>{feedName}</strong> and thousands of other feeds on{' '}
                <a href="https://downread.com">Downread</a>. No sign-up required to get started.
              </div>
              <div className="shareBannerActions">
                <button type="button" className="shareBannerCta" onClick={handleTryNow} disabled={trying}>
                  {trying ? 'Opening…' : 'Try now'}
                </button>
              </div>
            </div>
          </>
        )}
        <LegalFooter bottom />
      </div>
    </div>
  );
};

export default SubscribeFeedPage;
